/**
 * Patch builder: size × quantity calculator over a product's variant grid.
 * Variant titles read "<size> inch - <qty tier>" ("3.0 inch - 51-100",
 * "1.5\" - 25-49", "4 sq in - 100-199", "2.5 inch - 1000+"). Width/height
 * average snaps UP to the nearest size; quantity buckets into its tier; the
 * matching variant id is written into the (Horizon) product form, so the cart
 * charges exactly the price displayed.
 */
(() => {
  const TITLE = /^(\d+(?:\.\d+)?)\s*(?:inch|in|"|sq in)?\s*-\s*(\d+)(?:-(\d+)|(\+))?$/i;
  const money = (cents) => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat((window.Shopify && Shopify.locale) || 'en', { style: 'currency', currency }).format(cents / 100); }
    catch { return `$${(cents / 100).toFixed(2)}`; }
  };

  const escapeHtml = (str) => String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const shopRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

  /**
   * The background remover leaves its cutout in IndexedDB. Take it once, so nobody has to
   * download their own file and upload it again here. Anything older than half an hour
   * belongs to a forgotten tab, not to this trip.
   */
  function takeCutout() {
    return new Promise((resolve) => {
      if (!window.indexedDB || !window.DataTransfer) return resolve(null);
      let settled = false;
      const done = (value) => { if (!settled) { settled = true; resolve(value); } };
      setTimeout(() => done(null), 3000);
      let request;
      try { request = indexedDB.open('mcp-tools', 1); } catch (error) { return done(null); }
      request.onupgradeneeded = () => { try { request.result.createObjectStore('handoff'); } catch (error) { /* already there */ } };
      request.onerror = () => done(null);
      request.onsuccess = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains('handoff')) { db.close(); return done(null); }
        const tx = db.transaction('handoff', 'readwrite');
        const store = tx.objectStore('handoff');
        const read = store.get('cutout');
        read.onsuccess = () => {
          const held = read.result;
          store.delete('cutout');
          tx.oncomplete = () => { db.close(); done(held && held.blob && Date.now() - held.at < 1800000 ? held : null); };
        };
        read.onerror = () => { db.close(); done(null); };
      };
    });
  }

  function parse(variants) {
    const entries = [];
    for (const v of variants) {
      const m = TITLE.exec(v.t.trim());
      if (!m) continue;
      const min = Number(m[2]);
      const max = m[4] ? Infinity : (m[3] ? Number(m[3]) : min);
      entries.push({ id: v.id, price: v.p, size: parseFloat(m[1]), tier: { label: m[2] + (m[3] ? `-${m[3]}` : m[4] ? '+' : ''), min, max } });
    }
    const sizes = [...new Set(entries.map((e) => e.size))].sort((a, b) => a - b);
    const tiersMap = new Map();
    for (const e of entries) if (!tiersMap.has(e.tier.label)) tiersMap.set(e.tier.label, e.tier);
    const tiers = [...tiersMap.values()].sort((a, b) => a.min - b.min);
    return { entries, sizes, tiers, minQty: tiers.length ? tiers[0].min : 1 };
  }

  function init(root) {
    const data = JSON.parse(root.querySelector('[data-pb-variants]').textContent);
    const grid = parse(data);
    if (grid.sizes.length < 2 || grid.tiers.length < 2) return; // not a size×qty product
    // Theme settings → Cart minimum → Minimum quantity is the real floor when it is above the lowest tier
    grid.minQty = Math.max(grid.minQty, Number(root.dataset.storeMinQty) || 1);

    const q = (s) => root.querySelector(s);
    const W = q('[data-pb-w]'), H = q('[data-pb-h]'), QTY = q('[data-pb-qty]'), qtyErr = q('[data-pb-qty-err]');
    const variantInput = q('[data-pb-variant]');
    const propW = q('[data-pb-prop-w]'), propH = q('[data-pb-prop-h]'), propTier = q('[data-pb-prop-tier]');
    const each = q('[data-pb-each]'), total = q('[data-pb-total]'), totalHint = q('[data-pb-total-hint]'), save = q('[data-pb-save]');
    const stickyTotal = q('[data-pb-sticky-total]'), stickyHint = q('[data-pb-sticky-hint]');
    const anchorEl = q('[data-pb-anchor-price]'), anchorQtyEl = q('[data-pb-anchor-qty]'), sizeHint = q('[data-pb-size-hint]'), minHint = q('[data-pb-min-hint]');
    const tiersTable = q('[data-pb-tiers] tbody');
    const ladderRow = q('[data-pb-ladder-row]'), ladderSize = q('[data-pb-ladder-size]');
    // Backing surcharge (e.g. Velcro +$0.25/pc): charged through a hidden add-on product in the same quantity.
    const sur = root.dataset.surchargeVariant ? {
      variant: Number(root.dataset.surchargeVariant),
      price: Number(root.dataset.surchargePrice) || 0,
      match: (root.dataset.surchargeMatch || '').toLowerCase(),
      label: root.dataset.surchargeLabel || 'Backing surcharge',
    } : null;
    const extraEl = q('[data-pb-extra]');
    const recapEl = q('[data-pb-recap]'), breakEl = q('[data-pb-break]'), liveEl = q('[data-pb-live]');
    const fill = (tpl, map) => String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (k in map ? map[k] : m));
    let lastTotal = '', liveTimer;
    // Finished steps swap their number for a green tick.
    const markSteps = () => {
      const set = (k, on) => { const el = root.querySelector(`[data-pb-step="${k}"]`); if (el) el.classList.toggle('is-done', !!on); };
      set('size', parseFloat(W.value) > 0 && parseFloat(H.value) > 0);
      set('shape', root.querySelector('input[name="properties[Shape]"]:checked'));
      set('qty', Number(QTY.value) >= grid.minQty);
      const art = root.querySelector('[data-pb-file]');
      set('art', art && art.files && art.files.length);
      set('backing', root.querySelector('input[name="properties[Backing]"]:checked'));
      set('material', root.querySelector('[data-pb-step="material"] input:checked'));
      const colorGroups = [...root.querySelectorAll('[data-pb-colors]')];
      set('colors', colorGroups.length && colorGroups.every((g) => {
        const picked = g.querySelector('input[type="radio"]:checked');
        const custom = g.querySelector('[data-pb-color-custom-input]');
        return !!picked && (!picked.hasAttribute('data-pb-color-custom') || !!(custom && custom.value.trim()));
      }));
    };
    const surchargeOn = () => {
      if (!sur || !sur.match) return false;
      const picked = root.querySelector('input[name="properties[Backing]"]:checked');
      return !!picked && picked.value.toLowerCase().includes(sur.match);
    };
    const anchorQty = Number(root.dataset.anchorQty) || 100;
    const maxSize = grid.sizes[grid.sizes.length - 1], minSize = grid.sizes[0];
    // Area-priced products (DTF): variant sizes are square inches, so width and height follow the
    // block's inch limits and the price uses width × height.
    const areaMode = root.dataset.sizeMode === 'area';
    const dimMin = areaMode ? (Number(root.dataset.minSize) || 1) : minSize;
    const dimMax = areaMode ? (Number(root.dataset.maxSize) || 12) : maxSize;
    let lastW = null, lastH = null;

    const snapSize = (avg) => grid.sizes.find((s) => s >= avg - 1e-9) ?? maxSize;
    const findTier = (qty) => grid.tiers.find((t) => qty >= t.min && qty <= t.max) ?? (qty < grid.minQty ? grid.tiers[0] : grid.tiers[grid.tiers.length - 1]);
    const entry = (size, tier) => grid.entries.find((e) => e.size === size && e.tier.label === tier.label);

    // Tier label as the price table shows it
    // A tier that starts below the store minimum reads from the minimum (1–15 → 10–15)
    const tierFrom = (t) => Math.max(t.min, grid.minQty);
    const tierLabel = (t) => (t.max === Infinity || t.max >= 999999) ? `${tierFrom(t)}+` : tierFrom(t) === t.max ? `${t.max}` : `${tierFrom(t)}–${t.max}`;
    minHint.textContent = `Minimum ${grid.minQty} ${grid.minQty === 1 ? 'piece' : 'pieces'}`;

    function update(typing = false) {
      let w = Math.min(Math.max(parseFloat(W.value) || dimMin, dimMin), dimMax);
      let h = Math.min(Math.max(parseFloat(H.value) || dimMin, dimMin), dimMax);
      lastW = w; lastH = h;
      // Below the minimum: say so under the box. While typing the number is left alone (and the
      // minimum is priced); once the field is left it is raised to the minimum.
      const rawQty = QTY.value.trim() === '' ? null : Number(QTY.value);
      const shortQty = typing ? rawQty !== null && rawQty < grid.minQty : rawQty === null || rawQty < grid.minQty;
      if (qtyErr) {
        qtyErr.hidden = !shortQty;
        if (shortQty) qtyErr.textContent = typing
          ? `Minimum order is ${grid.minQty} pieces.`
          : `Minimum order is ${grid.minQty} pieces — quantity set to ${grid.minQty}.`;
      }
      let qty = Math.max(Math.round(Number(QTY.value) || grid.minQty), 1);
      if (qty < grid.minQty) { qty = grid.minQty; if (!typing) QTY.value = qty; }
      const size = snapSize(areaMode ? w * h : (w + h) / 2);
      const tier = findTier(qty);
      const e = entry(size, tier);
      if (!e) return;
      variantInput.value = e.id;
      propW.value = w; propH.value = h; propTier.value = areaMode ? `${size} sq in · ${tier.label} pcs` : `${size}" · ${tier.label} pcs`;
      sizeHint.textContent = areaMode ? `Priced as ${size} sq in (${w}" × ${h}")` : `Priced as ${size}" (avg of ${w}" × ${h}")`;
      const extra = surchargeOn() ? sur.price : 0;
      const unit = e.price + extra;
      each.textContent = money(unit);
      const totalText = money(unit * qty);
      if (lastTotal && lastTotal !== totalText) { total.classList.remove('is-bump'); void total.offsetWidth; total.classList.add('is-bump'); }
      lastTotal = totalText;
      total.textContent = totalText;
      totalHint.textContent = `${qty} pieces`;
      if (stickyTotal) { stickyTotal.textContent = money(unit * qty); stickyHint.textContent = `${qty} pcs · ${money(unit)} each`; }
      if (extraEl) {
        extraEl.hidden = !extra;
        if (extra) extraEl.innerHTML = `Includes ${escapeHtml(sur.label)} <b>+${money(extra)}</b> per patch`;
      }
      const first = entry(size, grid.tiers[0]);
      const firstUnit = first ? first.price + extra : 0;
      const pct = first && firstUnit > unit ? Math.round((1 - unit / firstUnit) * 100) : 0;
      const saveSentence = pct > 0 ? `You save ${pct}% vs. the ${tierLabel(grid.tiers[0])}-piece price` : '';
      save.textContent = pct > 0 ? `Save ${pct}%` : '';
      save.title = saveSentence;
      if (recapEl) {
        const shapeEl = root.querySelector('input[name="properties[Shape]"]:checked');
        const backingEl = root.querySelector('input[name="properties[Backing]"]:checked');
        const borderEl = root.querySelector('[name="properties[Border]"]');
        const materialEl = root.querySelector('[data-pb-step="material"] input:checked');
        recapEl.innerHTML = [
          `<li>${escapeHtml(`${w}" × ${h}"`)}</li>`,
          shapeEl ? `<li>${escapeHtml(shapeEl.value)}</li>` : (recapEl.dataset.missingShape ? `<li class="is-missing">${escapeHtml(recapEl.dataset.missingShape)}</li>` : ''),
          backingEl ? `<li>${escapeHtml(backingEl.value)}</li>` : '',
          materialEl ? `<li>${escapeHtml(materialEl.value)}</li>` : '',
          ...[...root.querySelectorAll('[data-pb-colors]')].map((g) => {
            const picked = g.querySelector('input[type="radio"]:checked');
            if (!picked || picked === g.querySelector('input[type="radio"]')) return '';
            const custom = g.querySelector('[data-pb-color-custom-input]');
            const text = picked.hasAttribute('data-pb-color-custom') && custom && custom.value.trim() ? custom.value.trim() : picked.value;
            return `<li>${escapeHtml(`${g.dataset.label}: ${text}`)}</li>`;
          }),
          borderEl && borderEl.value ? `<li>${escapeHtml(borderEl.value)}</li>` : '',
        ].join('');
      }
      if (breakEl) {
        // Price breaks around the current quantity: step down to the previous break point or up to the next one.
        // When the next break costs less in total than the current order, say so.
        const d = breakEl.dataset;
        const next = grid.tiers.find((t) => t.min > qty);
        const nextEntry = next ? entry(size, next) : null;
        const prevQty = Math.max(0, ...grid.tiers.map((t) => t.min).filter((m) => m < qty));
        const prevEntry = prevQty ? entry(size, findTier(prevQty)) : null;
        const icon = (path) => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="${path}"/></svg>`;
        const stepButton = (n, e, tpl, down) => {
          const label = escapeHtml(fill(tpl, { qty: n }));
          const title = escapeHtml(`${n} pieces at ${money(e.price + extra)} each`);
          return `<button type="button" class="pb__break-btn${down ? ' pb__break-btn--down' : ''}" data-pb-break-qty="${n}" title="${title}">`
            + (down ? `${icon('m15 18-6-6 6-6')}${label}` : `${label}${icon('m9 18 6-6-6-6')}`) + '</button>';
        };
        let text = '';
        if (nextEntry) {
          const nextUnit = nextEntry.price + extra;
          const nextTotal = nextUnit * next.min;
          const cheaper = nextTotal <= unit * qty ? `<small>${escapeHtml(fill(d.cheaper, { qty: next.min, total: money(nextTotal) }))}</small>` : '';
          text = fill(escapeHtml(d.text), { qty: next.min, price: `<b>${escapeHtml(money(nextUnit))}</b>` }) + cheaper;
        } else if (d.top) {
          text = fill(escapeHtml(d.top), { price: `<b>${escapeHtml(money(unit))}</b>` });
        }
        const buttons = (prevEntry ? stepButton(prevQty, prevEntry, d.down || d.button, true) : '')
          + (nextEntry ? stepButton(next.min, nextEntry, d.button, false) : '');
        breakEl.hidden = !(text || buttons);
        breakEl.innerHTML = text || buttons ? `<span class="pb__break-text">${text}</span><span class="pb__break-actions">${buttons}</span>` : '';
      }
      if (liveEl) {
        clearTimeout(liveTimer);
        liveTimer = setTimeout(() => { liveEl.textContent = `Total ${totalText} for ${qty} pieces, ${money(unit)} each. ${saveSentence}`; }, 600);
      }
      // Headline line mirrors the live selection: current per-piece price at the entered quantity.
      anchorEl.textContent = money(unit);
      if (anchorQtyEl) anchorQtyEl.textContent = qty;
      if (tiersTable) {
        tiersTable.innerHTML = grid.tiers.map((t) => {
          const te = entry(size, t);
          if (!te) return '';
          const tUnit = te.price + extra;
          const s = first && firstUnit > tUnit ? Math.round((1 - tUnit / firstUnit) * 100) : 0;
          return `<tr class="${t.label === tier.label ? 'is-current' : ''}" data-qty="${tierFrom(t)}"><td>${tierLabel(t)}</td><td>${money(tUnit)}</td><td class="pb__saving">${s ? `${s}%` : '—'}</td></tr>`;
        }).join('');
      }
      if (ladderRow) {
        // The ladder under the headline: every band at the size being priced, saving vs. the smallest run.
        ladderRow.innerHTML = grid.tiers.map((t) => {
          const te = entry(size, t);
          if (!te) return '';
          const tUnit = te.price + extra;
          const s = first && firstUnit > tUnit ? Math.round((1 - tUnit / firstUnit) * 100) : 0;
          return `<button type="button" class="pb__ladder-step${t.label === tier.label ? ' is-current' : ''}" data-pb-ladder-qty="${tierFrom(t)}"><span class="pb__ladder-qty">${tierLabel(t)}</span><b class="pb__ladder-price">${money(tUnit)}</b><span class="pb__ladder-off">${s ? `−${s}%` : '&nbsp;'}</span></button>`;
        }).join('');
        if (ladderSize) ladderSize.textContent = areaMode ? `${size} sq in` : `${size}"`;
      }
      markSteps();
    }

    // Artwork: preview + keep proportions (height follows width by the image's ratio)
    const file = q('[data-pb-file]'), lock = q('[data-pb-lock]'), preview = q('[data-pb-preview]'), fileName = q('[data-pb-file-name]'), drop = q('[data-pb-upload]');
    let ratio = null;
    if (file) {
      file.addEventListener('change', () => {
        const f = file.files && file.files[0];
        if (!f) { showArtColors(null); return; }
        fileName.textContent = f.name;
        if (f.type.startsWith('image/')) {
          const url = URL.createObjectURL(f);
          const img = new Image();
          img.onload = () => { ratio = img.naturalHeight / img.naturalWidth; preview.src = url; preview.style.display = 'block'; applyLock(); showArtColors(img); proofSource = img; proofArt = null; scheduleProof(); noteSmall(img, f); };
          img.onerror = () => showArtColors(null, true);
          img.src = url;
        } else { ratio = null; preview.style.display = 'none'; showArtColors(null); proofSource = null; if (proofRoot) proofRoot.hidden = true; noteSmall(null); }
      });
      ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-over'); }));
      ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, () => drop.classList.remove('is-over')));
      // Someone who just used the background remover arrives with their cutout in hand.
      takeCutout().then((held) => {
        if (!held) return;
        const carried = new DataTransfer();
        carried.items.add(new File([held.blob], held.name, { type: held.blob.type || 'image/png' }));
        file.files = carried.files;
        file.dispatchEvent(new Event('change', { bubbles: true }));
        // The instant patch proof also carries the size and shape the customer settled on.
        if (held.size && held.size.w) {
          if (lock) lock.checked = false;
          W.value = String(held.size.w); H.value = String(held.size.h || held.size.w);
          W.dispatchEvent(new Event('input', { bubbles: true })); H.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (held.shape) {
          const radios = Array.from(root.querySelectorAll('input[name="properties[Shape]"]'));
          const match = radios.find((r) => r.value.trim().toLowerCase() === String(held.shape).trim().toLowerCase());
          if (match) { match.checked = true; match.dispatchEvent(new Event('change', { bubbles: true })); }
        }
        markSteps();
      }).catch(() => {});
    }
    // Small artwork: say so, and offer the free upscaler with the file carried over and a way back.
    const smallEl = q('[data-pb-small]');
    let smallFile = null;
    function noteSmall(img, f) {
      if (!smallEl) return;
      const min = Number(smallEl.dataset.min) || 600;
      const long = img ? Math.max(img.naturalWidth, img.naturalHeight) : 0;
      smallFile = img && long < min ? f : null;
      smallEl.hidden = !smallFile;
      const textEl = smallEl.querySelector('[data-pb-small-text]');
      if (textEl && smallFile) textEl.textContent = (smallEl.dataset.text || '').replace('[px]', String(long));
    }
    function keepHandoff(key, record) {
      return new Promise((resolve) => {
        try {
          const open = indexedDB.open('mcp-tools', 1);
          open.onupgradeneeded = () => { try { open.result.createObjectStore('handoff'); } catch (e) { /* exists */ } };
          open.onerror = () => resolve();
          open.onsuccess = () => {
            const db = open.result;
            try {
              const tx = db.transaction('handoff', 'readwrite');
              tx.objectStore('handoff').put({ ...record, at: Date.now() }, key);
              tx.oncomplete = () => { db.close(); resolve(); };
              tx.onerror = () => { db.close(); resolve(); };
            } catch (e) { db.close(); resolve(); }
          };
        } catch (e) { resolve(); }
      });
    }
    if (smallEl) smallEl.addEventListener('click', (e) => {
      const link = e.target.closest('[data-pb-upscale]');
      if (!link || !smallFile) return;
      e.preventDefault();
      keepHandoff('upscale', { blob: smallFile, name: smallFile.name, returnTo: window.location.href }).then(() => { window.location.href = link.href; });
    });
    // Instant proof: the uploaded artwork drawn as this product's patch, right in the artwork step,
    // following the size, shape and border chosen above. The engine is assets/patch-proof.js.
    const proofRoot = q('[data-pb-proof]'), proofCanvas = q('[data-pb-proof-canvas]'), proofCut = q('[data-pb-proof-cutout]'), proofBackdrop = q('[data-pb-proof-backdrop]');
    let proofSource = null, proofArt = null, proofArtKey = '', proofTimer = null;
    const shapeKey = () => {
      const picked = root.querySelector('input[name="properties[Shape]"]:checked');
      const v = picked ? picked.value.toLowerCase() : '';
      if (v.includes('circle') || v.includes('round')) return 'circle';
      if (v.includes('square')) return 'square';
      if (v.includes('rect')) return 'rect';
      if (v.includes('oval')) return 'oval';
      if (v.includes('shield')) return 'shield';
      return 'contour';
    };
    const borderKey = (style) => {
      const sel = root.querySelector('[name="properties[Border]"]');
      if (sel && sel.value) return sel.value.toLowerCase().includes('merrow') ? 'merrowed' : 'hotcut';
      return style === 'embroidered' ? 'merrowed' : 'hotcut';
    };
    function scheduleProof() { clearTimeout(proofTimer); proofTimer = setTimeout(drawProof, 60); }
    function drawProof() {
      const Proof = window.MCPProof;
      if (!proofRoot || !proofCanvas || !proofSource || !Proof) return;
      const style = proofRoot.dataset.proofStyle || 'embroidered';
      const cut = !proofCut || proofCut.checked;
      const key = String(cut);
      if (!proofArt || proofArtKey !== key) {
        try { proofArt = Proof.prepareImage(proofSource, { cutout: cut, tolerance: 60 }); proofArtKey = key; }
        catch (e) { proofRoot.hidden = true; return; }
      }
      const w = Math.min(Math.max(parseFloat(W.value) || 3, 1), 15);
      const h = Math.min(Math.max(parseFloat(H.value) || w, 1), 15);
      const ground = style === 'leather' ? '#A3744A' : style === 'pvc' ? '#F1F2F6' : '#FFFFFF';
      const edge = style === 'leather' ? '#5C3A21' : Proof.dominant(proofArt);
      const dark = !!proofBackdrop && proofBackdrop.getAttribute('aria-pressed') === 'true';
      try {
        Proof.render({ art: proofArt, shape: shapeKey(), style, border: borderKey(style), borderColour: edge, twill: ground, widthIn: w, heightIn: h, backdrop: dark ? 'dark' : 'light' }, proofCanvas);
        proofRoot.hidden = false;
      } catch (e) { proofRoot.hidden = true; }
    }
    if (proofRoot) {
      if (proofCut) proofCut.addEventListener('change', scheduleProof);
      if (proofBackdrop) proofBackdrop.addEventListener('click', () => { proofBackdrop.setAttribute('aria-pressed', proofBackdrop.getAttribute('aria-pressed') === 'true' ? 'false' : 'true'); scheduleProof(); });
      [W, H].forEach((el) => el.addEventListener('input', scheduleProof));
      root.addEventListener('change', (e) => { if (e.target.name === 'properties[Shape]' || e.target.name === 'properties[Border]') scheduleProof(); });
    }
    const applyLock = () => {
      if (!lock || !lock.checked || !ratio) return;
      const w = parseFloat(W.value) || dimMin;
      H.value = (Math.round((w * ratio) * 2) / 2).toFixed(1).replace(/\.0$/, '');
      update();
    };
    W.addEventListener('input', () => { applyLock(); update(); });
    H.addEventListener('input', () => { if (lock && lock.checked && ratio) { const h = parseFloat(H.value) || dimMin; W.value = (Math.round((h / ratio) * 2) / 2).toFixed(1).replace(/\.0$/, ''); } update(); });
    QTY.addEventListener('input', () => update(true));
    QTY.addEventListener('change', () => update());
    // After typing a size this product isn't priced for, show the size that was priced.
    [W, H].forEach((el) => el.addEventListener('change', () => { update(); if (lastW !== null) { W.value = String(lastW); H.value = String(lastH); } }));
    if (file) file.addEventListener('change', markSteps);
    if (tiersTable) tiersTable.addEventListener('click', (e) => { const r = e.target.closest('tr[data-qty]'); if (r) { QTY.value = r.dataset.qty; update(); } });
    if (ladderRow) ladderRow.addEventListener('click', (e) => { const btn = e.target.closest('[data-pb-ladder-qty]'); if (btn) { QTY.value = btn.dataset.pbLadderQty; update(); } });
    if (breakEl) breakEl.addEventListener('click', (e) => { const btn = e.target.closest('[data-pb-break-qty]'); if (btn) { QTY.value = btn.dataset.pbBreakQty; update(); } });
    // The recap shows shape, colour and border, so those choices refresh the price box too.
    root.addEventListener('change', (e) => { if (e.target.name === 'properties[Shape]' || e.target.name === 'properties[Border]' || e.target.closest('[data-pb-step="material"]')) update(); });
    // Colour pickers: name the pick beside the picker label, and open a text box for Custom.
    root.querySelectorAll('[data-pb-colors]').forEach((g) => {
      const nameEl = g.querySelector('[data-pb-color-name]');
      const custom = g.querySelector('[data-pb-color-custom-input]');
      const sync = () => {
        const picked = g.querySelector('input[type="radio"]:checked');
        const isCustom = !!picked && picked.hasAttribute('data-pb-color-custom');
        if (custom) { custom.hidden = !isCustom; custom.disabled = !isCustom; }
        const typed = isCustom && custom ? custom.value.trim() : '';
        if (nameEl) nameEl.textContent = picked ? (typed ? `${picked.value}: ${typed}` : picked.value) : '';
      };
      g.addEventListener('change', (e) => {
        sync(); update();
        if (custom && e.target.hasAttribute('data-pb-color-custom')) custom.focus();
      });
      if (custom) custom.addEventListener('input', () => { sync(); update(); });
      g.pbSync = sync;
      sync();
    });
    // Artwork colours: the uploaded image's main colours become swatches in both pickers.
    const dominantColors = (img, max) => {
      const w0 = img.naturalWidth || 96, h0 = img.naturalHeight || 96;
      const scale = Math.min(1, 96 / Math.max(w0, h0));
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(w0 * scale));
      canvas.height = Math.max(1, Math.round(h0 * scale));
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const px = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
      const bins = new Map();
      let opaque = 0;
      for (let i = 0; i < px.length; i += 4) {
        if (px[i + 3] < 160) continue;
        opaque += 1;
        const key = ((px[i] >> 4) << 8) | ((px[i + 1] >> 4) << 4) | (px[i + 2] >> 4);
        const bin = bins.get(key) || { n: 0, exact: new Map() };
        const rgbKey = (px[i] << 16) | (px[i + 1] << 8) | px[i + 2];
        bin.n += 1;
        bin.exact.set(rgbKey, (bin.exact.get(rgbKey) || 0) + 1);
        bins.set(key, bin);
      }
      // "Redmean" distance: close enough to be the same ink, so shading and anti-aliasing merge into one swatch.
      const same = (a, b) => {
        const rm = (a[0] + b[0]) / 2, dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2];
        return Math.sqrt((2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db) < 90;
      };
      const picks = [];
      [...bins.values()].sort((a, b) => b.n - a.n).forEach((bin) => {
        // Each bin's most frequent exact colour keeps flat artwork colours exact; anti-aliased edges would skew an average.
        const top = [...bin.exact.entries()].reduce((a, b) => (b[1] > a[1] ? b : a))[0];
        const rgb = [(top >> 16) & 255, (top >> 8) & 255, top & 255];
        const match = picks.find((pk) => same(pk.rgb, rgb));
        if (match) match.n += bin.n;
        else if (bin.n >= opaque * 0.015) picks.push({ rgb, n: bin.n });
      });
      return picks.sort((a, b) => b.n - a.n).slice(0, max)
        .map((pk) => `#${pk.rgb.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('').toUpperCase()}`);
    };
    const showArtColors = (img, failed) => {
      const groups = [...root.querySelectorAll('[data-pb-colors]')];
      if (!groups.length) return;
      let hexes = [];
      if (img) { try { hexes = dominantColors(img, 4); } catch (err) { hexes = []; } }
      const show = hexes.length > 0;
      groups.forEach((g) => {
        const row = g.querySelector('[data-pb-art-row]');
        const first = g.querySelector('input[type="radio"]');
        if (!row || !first) return;
        const was = g.querySelector('input[data-pb-art-color]:checked');
        row.replaceChildren(...hexes.map((hex) => {
          const label = document.createElement('label');
          label.className = 'pb__cswatch';
          label.title = hex;
          const input = document.createElement('input');
          input.type = 'radio'; input.name = first.name; input.value = hex;
          input.setAttribute('aria-label', hex);
          input.setAttribute('data-pb-art-color', '');
          input.checked = !!was && was.value === hex;
          const dot = document.createElement('span');
          dot.setAttribute('aria-hidden', 'true');
          dot.style.setProperty('--sw', hex);
          label.append(input, dot);
          return label;
        }));
        row.hidden = !show;
        g.querySelectorAll('[data-pb-art-sub], [data-pb-std-sub]').forEach((el) => { el.hidden = !show; });
        const note = g.querySelector('[data-pb-art-note]');
        if (note) note.hidden = show || !(img || failed);
        if (!g.querySelector('input[type="radio"]:checked')) first.checked = true;
        if (typeof g.pbSync === 'function') g.pbSync();
      });
      update();
    };
    // Leave blank extras (notes, no artwork chosen) out of the cart line entirely.
    const form = root.querySelector('form');
    if (form) {
      const dropEmpties = () => {
        const empties = [...form.querySelectorAll('[name^="properties["]')].filter((el) =>
          (el.type === 'file' && !(el.files && el.files.length)) || (el.type !== 'file' && !String(el.value || '').trim()));
        empties.forEach((el) => { el.disabled = true; });
        setTimeout(() => empties.forEach((el) => { el.disabled = false; }), 0);
      };
      const currentQty = () => Math.max(Math.round(Number(QTY.value) || grid.minQty), grid.minQty);
      let pendingAddon = null;
      const addonItem = (qty) => ({ id: sur.variant, quantity: qty, properties: { For: `${propW.value}" × ${propH.value}" ${(root.dataset.productTitle || 'patches').toLowerCase()}` } });
      root.pbGo = root.pbGo || ((url) => { window.location.href = url; });
      async function addAddon(qty) {
        const ids = [...document.querySelectorAll('cart-items-component')].map((c) => c.dataset.sectionId).filter(Boolean);
        const res = await fetch(`${shopRoot}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [addonItem(qty)], sections: ids.join(','), sections_url: window.location.pathname }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.status) { console.warn('[patch-builder] surcharge line not added', json); return; }
        try {
          const cart = await fetch(`${shopRoot}cart.js`).then((r) => r.json());
          const { CartLinesUpdateEvent } = await import('@shopify/events');
          const deferred = CartLinesUpdateEvent.createPromise();
          const ev = new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(sur.variant), quantity: qty }], promise: deferred.promise });
          ev.__pbAddon = true;
          document.dispatchEvent(ev);
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { items: cart.items, source: 'patch-builder', sourceId: root.id, itemCount: qty, sections: json.sections, didError: false } });
        } catch (err) { console.warn('[patch-builder] cart UI refresh failed', err); }
      }
      async function buyNowWithAddon(qty) {
        const wrap = root.querySelector('.shopify-payment-button');
        if (wrap) { wrap.style.pointerEvents = 'none'; wrap.style.opacity = '.6'; }
        const fail = (msg) => {
          if (wrap) { wrap.style.pointerEvents = ''; wrap.style.opacity = ''; }
          if (extraEl) { extraEl.hidden = false; extraEl.innerHTML = `<b>${escapeHtml(msg)}</b>`; }
        };
        try {
          dropEmpties();
          const r1 = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } });
          const j1 = await r1.json().catch(() => ({}));
          if (!r1.ok || j1.status) return fail(j1.description || j1.message || 'Could not add to cart.');
          const r2 = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [addonItem(qty)] }) });
          if (!r2.ok) return fail('Could not add the backing surcharge. Please use Add to cart.');
          root.pbGo(`${shopRoot}checkout`);
        } catch (err) { fail('Network error. Please try again.'); }
      }
      // Run on the button click (before the theme reads the form) and on submit (Enter key) as a fallback.
      root.addEventListener('click', (e) => {
        if (!e.target.closest('button[type="submit"], .shopify-payment-button')) return;
        const shapeGroup = root.querySelector('[data-pb-shape-required]');
        if (shapeGroup && !shapeGroup.querySelector('input:checked')) {
          e.preventDefault(); e.stopPropagation();
          shapeGroup.classList.add('is-invalid');
          const err = shapeGroup.querySelector('[data-pb-shape-err]'); if (err) err.hidden = false;
          shapeGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }
        if (surchargeOn()) {
          const job = { qty: currentQty(), at: Date.now() };
          // Buy it now skips the cart, so it would miss the add-on line: send both to the cart, then to checkout.
          if (e.target.closest('.shopify-payment-button')) { e.preventDefault(); e.stopPropagation(); buyNowWithAddon(job.qty); return; }
          pendingAddon = job;
        } else {
          pendingAddon = null;
        }
        dropEmpties();
      }, true);
      root.addEventListener('change', (e) => {
        if (e.target.name !== 'properties[Shape]') return;
        const g = e.target.closest('[data-pb-shape]'); if (!g) return;
        g.classList.remove('is-invalid'); const err = g.querySelector('[data-pb-shape-err]'); if (err) err.hidden = true;
        markSteps();
      });
      form.addEventListener('submit', () => {
        if (!surchargeOn()) pendingAddon = null;
        else if (!pendingAddon || Date.now() - pendingAddon.at > 20000) pendingAddon = { qty: currentQty(), at: Date.now() };
        dropEmpties();
      }, true);
      root.addEventListener('change', (e) => { if (e.target.name === 'properties[Backing]') update(); });
      // After the theme adds the patch line, add the surcharge line in the same quantity.
      root.addEventListener('shopify:cart:lines-update', (ev) => {
        const job = pendingAddon;
        if (!job || ev.__pbAddon || Date.now() - job.at > 20000) return;
        pendingAddon = null;
        if (ev.promise) ev.promise.then((res) => { if (!(res && res.detail && res.detail.didError)) addAddon(job.qty); }).catch(() => {});
      });
    }
    update();
    const sticky = q('[data-pb-sticky]'), atcWrap = q('.product-form-buttons');
    if (sticky && atcWrap && 'IntersectionObserver' in window) {
      new IntersectionObserver(([en]) => {
        const on = !en.isIntersecting;
        sticky.hidden = !on;
        document.documentElement.classList.toggle('pb-sticky-on', on);
      }, { threshold: 0 }).observe(atcWrap);
    }
    const guideBtn = q('[data-pb-guide]'), guideDlg = q('[data-pb-guide-dialog]');
    if (guideBtn && guideDlg && typeof guideDlg.showModal === 'function') {
      guideBtn.addEventListener('click', () => guideDlg.showModal());
      guideDlg.addEventListener('click', (e) => { if (e.target === guideDlg || e.target.closest('[data-pb-guide-close]')) guideDlg.close(); });
    }
  }

  const boot = () => document.querySelectorAll('.patch-builder:not([data-pb-ready])').forEach((el) => { el.dataset.pbReady = '1'; init(el); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);
})();
